import React, { useState, useEffect, useRef } from 'react';
import './ContactSalesModal.css';
import apiService from '../services/apiService';

const ContactSalesModal = ({ onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const modalRef = useRef(null);
  const firstInputRef = useRef(null);

  // Focus management and accessibility
  useEffect(() => {
    // Focus first input when modal opens
    if (firstInputRef.current) {
      firstInputRef.current.focus();
    }

    // Trap focus within modal
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
      
      if (e.key === 'Tab') {
        const focusableElements = modalRef.current?.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        
        if (focusableElements) {
          const firstElement = focusableElements[0];
          const lastElement = focusableElements[focusableElements.length - 1];
          
          if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          } else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Enhanced validation
  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Full name is required';
    } else if (formData.name.trim().length < 2) {
      errors.name = 'Name must be at least 2 characters';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (!formData.company.trim()) {
      errors.company = 'Company name is required';
    }
    
    return errors;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear errors as user types
    if (error) setError('');
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      // Focus first error field
      const firstErrorField = Object.keys(errors)[0];
      const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
      errorElement?.focus();
      return;
    }
    
    setIsLoading(true);
    setError('');
    setValidationErrors({});
    
    try {
      // Create enterprise contact data
      const contactData = {
        ...formData,
        plan: 'enterprise'
      };
      
      await apiService.createEnterpriseContact(contactData);
      setIsSubmitted(true);
    } catch (error) {
      console.error('Enterprise contact failed:', error);
      setError(error.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const CloseIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" y1="6" x2="6" y2="18"></line>
      <line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>
  );

  const LoadingSpinner = () => (
    <svg className="spinner" width="16" height="16" viewBox="0 0 24 24">
      <circle className="spinner-path" cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray="32" strokeDashoffset="32">
        <animate attributeName="stroke-dasharray" dur="2s" values="0 64;32 32;0 64" repeatCount="indefinite"/>
        <animate attributeName="stroke-dashoffset" dur="2s" values="0;-32;-64" repeatCount="indefinite"/>
      </circle>
    </svg>
  );

  if (isSubmitted) {
    return (
      <div className="modal-overlay" onClick={handleOverlayClick} role="dialog" aria-modal="true" aria-labelledby="success-title">
        <div className="modal-content success-modal" ref={modalRef}>
          <button 
            className="close-button" 
            onClick={onClose}
            aria-label="Close modal"
            type="button"
          >
            <CloseIcon />
          </button>
          
          <div className="success-content">
            <div className="success-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6,12 10,16 18,8"></polyline>
              </svg>
            </div>

            <h2 id="success-title">Message Received</h2>
            <p>
              Thank you for reaching out. Our sales team will review your inquiry and get back to you within one business day.
            </p>

            <div className="next-steps">
              <h3>What Happens Next</h3>
              <ul>
                <li>
                  <svg className="step-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                  A member of our team will reach out to you directly
                </li>
                <li>
                  <svg className="step-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                  We'll discuss your requirements and use cases
                </li>
                <li>
                  <svg className="step-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2"/></svg>
                  You'll receive a tailored proposal
                </li>
              </ul>
            </div>

            <div className="action-buttons">
              <button
                className="primary-button"
                onClick={onClose}
                type="button"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={handleOverlayClick} role="dialog" aria-modal="true" aria-labelledby="modal-title">
      <div className="modal-content contact-sales-modal" ref={modalRef}>
        <button 
          className="close-button" 
          onClick={onClose}
          aria-label="Close modal"
          type="button"
        >
          <CloseIcon />
        </button>
        
        <div className="modal-header">
          <h2 id="modal-title">Contact Sales</h2>
          <p>Get in touch with our enterprise team to discuss your custom requirements.</p>
        </div>
        
        <div className="enterprise-features">
          <h3>Enterprise Features</h3>
          <ul>
            <li>✓ On-premise deployment</li>
            <li>✓ Custom AI training</li>
            <li>✓ Dedicated support manager</li>
            <li>✓ Custom integrations</li>
            <li>✓ Advanced security</li>
          </ul>
        </div>
        
        <form onSubmit={handleSubmit} className="contact-form" noValidate>
          {error && (
            <div className="error-message" role="alert" aria-live="polite">
              <span className="error-icon" aria-hidden="true">⚠️</span>
              {error}
            </div>
          )}
          
          <div className="form-group">
            <label htmlFor="name">
              Full Name <span className="required" aria-label="required">*</span>
            </label>
            <input
              ref={firstInputRef}
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              placeholder="Enter your full name"
              aria-describedby={validationErrors.name ? 'name-error' : undefined}
              aria-invalid={validationErrors.name ? 'true' : 'false'}
              autoComplete="name"
            />
            {validationErrors.name && (
              <div id="name-error" className="field-error" role="alert">
                {validationErrors.name}
              </div>
            )}
          </div>
          
          <div className="form-group">
            <label htmlFor="email">
              Work Email <span className="required" aria-label="required">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              placeholder="your.email@company.com"
              aria-describedby={validationErrors.email ? 'email-error' : undefined}
              aria-invalid={validationErrors.email ? 'true' : 'false'}
              autoComplete="email"
            />
            {validationErrors.email && (
              <div id="email-error" className="field-error" role="alert">
                {validationErrors.email}
              </div>
            )}
          </div>
          
          <div className="form-group">
            <label htmlFor="company">
              Company <span className="required" aria-label="required">*</span>
            </label>
            <input
              type="text"
              id="company"
              name="company"
              value={formData.company}
              onChange={handleInputChange}
              required
              placeholder="Your company name"
              aria-describedby={validationErrors.company ? 'company-error' : undefined}
              aria-invalid={validationErrors.company ? 'true' : 'false'}
              autoComplete="organization"
            />
            {validationErrors.company && (
              <div id="company-error" className="field-error" role="alert">
                {validationErrors.company}
              </div>
            )}
          </div>
          
          <div className="form-group">
            <label htmlFor="message">
              Message (Optional)
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              rows="4"
              placeholder="Tell us about your requirements, team size, or any specific needs..."
            />
          </div>
          
          <button 
            type="submit" 
            className={`submit-button ${isLoading ? 'loading' : ''}`}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <LoadingSpinner />
                <span>Sending...</span>
              </>
            ) : (
              <>
                <span>Send Message</span>
                <span className="button-arrow" aria-hidden="true">→</span>
              </>
            )}
          </button>
          
          <p className="terms-note">
            By contacting us, you agree to our{' '}
            <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>{' '}
            and <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
          </p>
        </form>
      </div>
    </div>
  );
};

export default ContactSalesModal;